import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { ROLE_LABELS, ROLE_COLORS } from './CompanyContext';
import { useImpersonation } from './ImpersonationContext';
import { logAudit } from './auditLog';
import UpgradeLimitModal from './components/UpgradeLimitModal';
import './Users.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface MyProfile {
  id: string;
  company_id: string | null;
  full_name: string | null;
  email: string | null;
  role: 'admin' | 'manager' | 'viewer' | 'superadmin';
  active: boolean;
}

interface CompanyOption {
  id:               string;
  name:             string;
  corporate_domain: string | null;
}

interface MyCompany {
  id:               string;
  plan:             string;
  max_users:        number | null;
  corporate_domain: string | null;
}

interface UserProfile {
  id: string;
  full_name: string | null;
  email: string | null;
  role: 'admin' | 'manager' | 'viewer';
  active: boolean;
  created_at: string;
}

interface Invitation {
  id: string;
  email: string;
  role: 'admin' | 'manager' | 'viewer';
  token: string;
  created_at: string;
}

const ROLE_DESCS: Record<string, string> = {
  admin:   'Acceso total, gestión de usuarios',
  manager: 'Gestiona productos y bodegas',
  viewer:  'Solo lectura',
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function Users() {
  const { impersonated } = useImpersonation();

  // Own profile loaded independently — no CompanyContext dependency
  const [myProfile, setMyProfile]       = useState<MyProfile | null>(null);
  const [myCompany, setMyCompany]       = useState<MyCompany | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [users, setUsers]             = useState<UserProfile[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Superadmin: lista de empresas para el selector
  const [allCompanies,    setAllCompanies]    = useState<CompanyOption[]>([]);
  const [inviteCompanyId, setInviteCompanyId] = useState('');

  const [inviteOpen, setInviteOpen]     = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [inviteEmail, setInviteEmail]   = useState('');
  const [inviteRole, setInviteRole]     = useState<'admin' | 'manager' | 'viewer'>('viewer');
  const [inviting, setInviting]         = useState(false);
  const [inviteError, setInviteError]   = useState('');
  const [createdToken, setCreatedToken] = useState<string | null>(null);

  const [changingRole, setChangingRole]   = useState<string | null>(null);
  const [togglingId, setTogglingId]       = useState<string | null>(null);
  const [deletingInvId, setDeletingInvId] = useState<string | null>(null);
  const [copiedId, setCopiedId]           = useState<string | null>(null);
  const [toast, setToast]                 = useState('');

  // Delete user (confirmation modal)
  const [confirmDeleteUser, setConfirmDeleteUser] = useState<UserProfile | null>(null);
  const [deletingUserId, setDeletingUserId]       = useState<string | null>(null);

  // ── Load own profile on mount ──────────────────────────────────────────────

  useEffect(() => {
    async function loadProfile() {
      const { data: { user }, error: authErr } = await supabase.auth.getUser();

      if (authErr || !user) {
        console.error('[Users] getUser failed:', authErr?.message);
        setProfileLoading(false);
        return;
      }

      const { data: prof, error: profErr } = await supabase
        .from('profiles')
        .select('id, company_id, full_name, email, role, active')
        .eq('id', user.id)
        .maybeSingle();

      if (profErr) {
        console.error('[Users] profiles query failed:', profErr.message);
        setProfileLoading(false);
        return;
      }

      setMyProfile(prof ?? null);

      if (prof?.role === 'superadmin') {
        // Superadmin: carga lista de todas las empresas para el selector de invitación
        const { data: cos } = await supabase
          .from('companies')
          .select('id, name, corporate_domain')
          .eq('active', true)
          .order('name', { ascending: true });
        const list = (cos ?? []) as CompanyOption[];
        setAllCompanies(list);
        if (list.length > 0) setInviteCompanyId(list[0].id);
      } else if (prof?.company_id) {
        const { data: co } = await supabase
          .from('companies')
          .select('id, plan, max_users, corporate_domain')
          .eq('id', prof.company_id)
          .maybeSingle();
        setMyCompany(co ?? null);
      }

      setProfileLoading(false);
    }

    loadProfile();
  }, []);

  // ── Load users + invitations ───────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!myProfile) return;
    // Superadmin impersonating → filter by that company
    // Regular users (admin/manager/viewer) → always filter by own company_id
    // Superadmin NOT impersonating → no filter (sees all for admin panel)
    const filterCid = impersonated?.id
      ?? (myProfile.role === 'superadmin' ? null : myProfile.company_id);

    setDataLoading(true);
    const [{ data: profileData }, { data: inviteData }] = await Promise.all([
      (() => {
        let q = supabase
          .from('profiles')
          .select('id, full_name, email, role, active, created_at')
          .order('created_at', { ascending: true });
        if (filterCid) q = q.eq('company_id', filterCid);
        return q;
      })(),
      (() => {
        let q = supabase
          .from('invitations')
          .select('id, email, role, token, created_at')
          .is('accepted_at', null)
          .order('created_at', { ascending: false });
        if (filterCid) q = q.eq('company_id', filterCid);
        return q;
      })(),
    ]);
    setUsers(profileData ?? []);
    setInvitations(inviteData ?? []);
    setDataLoading(false);
  }, [impersonated, myProfile]);

  // Superadmin sin impersonar: no hay empresa propia, omitir carga
  useEffect(() => {
    if (myProfile && (myProfile.role !== 'superadmin' || impersonated)) fetchData();
  }, [myProfile, impersonated, fetchData]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const isSuperAdmin = myProfile?.role === 'superadmin';
  const isAdmin      = myProfile?.role === 'admin' || isSuperAdmin;
  const totalSlots   = users.length + invitations.length;
  // When impersonating, use that company's limits; otherwise use own company's
  const maxUsers     = impersonated?.max_users ?? myCompany?.max_users ?? null;
  const atLimit      = !isSuperAdmin && maxUsers !== null && totalSlots >= maxUsers;
  const usagePercent = maxUsers ? Math.min(100, Math.round((totalSlots / maxUsers) * 100)) : null;

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!myProfile) return;
    const email = inviteEmail.trim().toLowerCase();
    if (!email) { setInviteError('El correo es obligatorio.'); return; }

    // Validación de dominio corporativo
    const invitedDomain = email.split('@')[1] ?? '';
    if (isSuperAdmin) {
      // Superadmin: validar contra el dominio de la empresa destino
      const targetDomain = impersonated?.corporate_domain
        ?? allCompanies.find((c) => c.id === inviteCompanyId)?.corporate_domain;
      if (targetDomain && invitedDomain !== targetDomain) {
        setInviteError(`El usuario invitado debe pertenecer al mismo dominio corporativo (@${targetDomain})`);
        return;
      }
    } else {
      // Admin normal: validar contra su propio dominio de email
      const adminDomain = myProfile?.email?.split('@')[1] ?? '';
      if (adminDomain && invitedDomain !== adminDomain) {
        setInviteError(`El usuario invitado debe pertenecer al mismo dominio corporativo (@${adminDomain})`);
        return;
      }
    }

    // Superadmin: validar que eligió empresa
    if (isSuperAdmin && !inviteCompanyId) {
      setInviteError('Debes seleccionar una empresa para el invitado.');
      return;
    }

    const targetCompanyId = isSuperAdmin ? inviteCompanyId : myProfile.company_id;

    if (atLimit) {
      setInviteError(`Plan ${myCompany?.plan ?? 'actual'} permite ${maxUsers} usuarios. Actualiza tu plan para agregar más.`);
      return;
    }

    // Para roles normales: validar duplicados con la lista local
    // Para superadmin: la lista local está vacía (no se carga fetchData), la BD captura el unique constraint
    if (!isSuperAdmin) {
      const alreadyExists =
        users.some((u) => u.email?.toLowerCase() === email) ||
        invitations.some((i) => i.email.toLowerCase() === email);

      if (alreadyExists) {
        setInviteError('Este correo ya tiene acceso o tiene una invitación pendiente.');
        return;
      }
    }

    setInviting(true);
    setInviteError('');

    const { data, error } = await supabase
      .from('invitations')
      .insert({ company_id: targetCompanyId, email, role: inviteRole, invited_by: myProfile.id })
      .select('token')
      .single();

    if (error) {
      setInviteError(
        error.message.includes('unique')
          ? 'Ya existe una invitación para este correo.'
          : 'Error al crear invitación: ' + error.message,
      );
      setInviting(false);
      return;
    }

    // Send invite email via Edge Function
    const { error: fnError } = await supabase.functions.invoke('invite-user', {
      body: {
        email,
        role: inviteRole,
        company_id: targetCompanyId,
        token: data.token,
        redirectTo: window.location.origin,
      },
    });

    if (fnError) {
      let limitReached = false;
      try {
        const ctx = (fnError as any).context;
        if (ctx) {
          const bodyText = await ctx.clone().text();
          limitReached = bodyText.includes('user_limit_reached');
        }
      } catch {
        // best-effort parse, fall through to generic error below
      }

      if (limitReached) {
        // Roll back the invitation row created above — the plan limit was reached
        await supabase.from('invitations').delete().eq('token', data.token);
        setInviteError('Has alcanzado el límite de usuarios de tu plan. Actualiza tu plan para invitar más personas.');
        setInviting(false);
        return;
      }

      // Invitation record created but email failed — show link as fallback
      setInviteError('Invitación creada pero no se pudo enviar el correo. Comparte el enlace manualmente.');
    }

    const cid = isSuperAdmin ? inviteCompanyId : (myProfile?.company_id ?? '');
    if (cid) logAudit({ company_id: cid, action: 'created', entity_type: 'invitation', entity_id: data.token, entity_name: email, details: { role: inviteRole } });
    setCreatedToken(data.token);
    fetchData();
    setInviting(false);
  };

  const changeRole = async (userId: string, newRole: 'admin' | 'manager' | 'viewer') => {
    setChangingRole(userId);
    const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', userId);
    if (error) {
      showToast('Error al cambiar rol.');
    } else {
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)));
      showToast('Rol actualizado.');
    }
    setChangingRole(null);
  };

  const toggleActive = async (userId: string, currentActive: boolean) => {
    setTogglingId(userId);
    const { error } = await supabase.from('profiles').update({ active: !currentActive }).eq('id', userId);
    if (error) {
      showToast('Error al cambiar estado.');
    } else {
      const cid = impersonated?.id ?? myProfile?.company_id ?? '';
      const target = users.find(u => u.id === userId);
      if (cid) logAudit({ company_id: cid, action: 'updated', entity_type: 'user', entity_id: userId, entity_name: target?.full_name ?? target?.email ?? userId, details: { active: !currentActive } });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, active: !currentActive } : u)));
      showToast(!currentActive ? 'Usuario activado.' : 'Usuario desactivado.');
    }
    setTogglingId(null);
  };

  const handleDeleteUser = async () => {
    if (!confirmDeleteUser) return;
    const target = confirmDeleteUser;
    setDeletingUserId(target.id);
    const { error } = await supabase.from('profiles').delete().eq('id', target.id);
    if (error) {
      showToast('Error al eliminar usuario: ' + error.message);
    } else {
      const cid = impersonated?.id ?? myProfile?.company_id ?? '';
      if (cid) logAudit({ company_id: cid, action: 'deleted', entity_type: 'user', entity_id: target.id, entity_name: displayName(target) });
      setUsers((prev) => prev.filter((u) => u.id !== target.id));
      showToast('Usuario eliminado.');
      setConfirmDeleteUser(null);
    }
    setDeletingUserId(null);
  };

  const cancelInvitation = async (id: string) => {
    setDeletingInvId(id);
    const inv = invitations.find(i => i.id === id);
    const { error } = await supabase.from('invitations').delete().eq('id', id);
    if (error) {
      showToast('Error al cancelar invitación.');
    } else {
      const cid = impersonated?.id ?? myProfile?.company_id ?? '';
      if (cid) logAudit({ company_id: cid, action: 'deleted', entity_type: 'invitation', entity_id: id, entity_name: inv?.email ?? id });
      setInvitations((prev) => prev.filter((i) => i.id !== id));
      showToast('Invitación cancelada.');
    }
    setDeletingInvId(null);
  };

  const copyLink = async (token: string, id: string) => {
    const url = `${window.location.origin}?join=${token}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2500);
  };

  const openInviteModal = () => {
    if (isSuperAdmin && impersonated) setInviteCompanyId(impersonated.id);
    setInviteOpen(true);
  };

  const closeInviteModal = () => {
    setInviteOpen(false);
    setInviteEmail('');
    setInviteRole('viewer');
    setInviteError('');
    setCreatedToken(null);
    if (isSuperAdmin) setInviteCompanyId(impersonated?.id ?? allCompanies[0]?.id ?? '');
  };

  const displayName = (u: UserProfile) =>
    u.full_name || u.email?.split('@')[0] || 'Sin nombre';

  // ── Render: loading / no profile ───────────────────────────────────────────

  if (profileLoading) {
    return (
      <div className="usr-loading" style={{ paddingTop: 64 }}>
        <span className="usr-spinner" />
        <p>Cargando perfil…</p>
      </div>
    );
  }

  if (!myProfile) {
    return (
      <div className="usr-setup-required">
        <SetupIcon />
        <p className="usr-setup-title">Perfil no encontrado</p>
        <p className="usr-setup-sub">
          Crea un registro en la tabla <code>profiles</code> vinculado a tu usuario de Supabase Auth.
        </p>
      </div>
    );
  }

  // ── Render: main UI ────────────────────────────────────────────────────────

  return (
    <div className="usr-root">

      {/* ── Header ── */}
      <div className="usr-header">
        <div>
          <h3 className="usr-title">
            Usuarios{impersonated ? ` · ${impersonated.name}` : ''}
          </h3>
          <p className="usr-subtitle">
            {users.length} usuario{users.length !== 1 ? 's' : ''} activo{users.length !== 1 ? 's' : ''}
            {invitations.length > 0 && ` · ${invitations.length} invitación${invitations.length !== 1 ? 'es' : ''} pendiente${invitations.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="usr-header-right">
          {maxUsers !== null && (
            <div className="usr-plan-counter">
              <span className={`usr-plan-nums ${atLimit ? 'at-limit' : ''}`}>
                {totalSlots}/{maxUsers}
              </span>
              <span className="usr-plan-label">usuarios</span>
              {usagePercent !== null && (
                <div className="usr-mini-bar">
                  <div
                    className="usr-mini-bar-fill"
                    style={{
                      width: `${usagePercent}%`,
                      background: usagePercent > 90 ? '#ef4444' : usagePercent > 70 ? '#f59e0b' : '#22c55e',
                    }}
                  />
                </div>
              )}
            </div>
          )}
          {isAdmin && (
            <button
              className="usr-btn-invite"
              onClick={() => { if (atLimit) setShowUpgradeModal(true); else openInviteModal(); }}
              title={atLimit ? `Límite del plan alcanzado (${maxUsers} usuarios)` : 'Invitar nuevo usuario'}
            >
              <PlusIcon /> Invitar usuario
            </button>
          )}
        </div>
      </div>

      {showUpgradeModal && (
        <UpgradeLimitModal
          resource="users"
          currentUsage={totalSlots}
          plan={impersonated?.plan ?? myCompany?.plan}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}

      {/* ── Limit banner ── */}
      {atLimit && isAdmin && (
        <div className="usr-limit-banner">
          <LockIcon />
          <div>
            <strong>Límite del plan alcanzado</strong>
            <p>Tu plan {myCompany?.plan ?? 'actual'} permite máximo {maxUsers} usuarios. Actualiza tu plan para agregar más.</p>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="usr-table-wrap">
        {dataLoading ? (
          <div className="usr-loading">
            <span className="usr-spinner" />
            <p>Cargando usuarios…</p>
          </div>
        ) : users.length === 0 && invitations.length === 0 ? (
          <div className="usr-empty">
            <EmptyUsersIcon />
            <p className="usr-empty-title">Sin usuarios</p>
            <p className="usr-empty-sub">Invita a los miembros de tu equipo.</p>
          </div>
        ) : (
          <table className="usr-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Miembro desde</th>
                {isAdmin && <th style={{ width: 180 }}>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = u.id === myProfile.id;
                const rc   = ROLE_COLORS[u.role];
                return (
                  <tr key={u.id} className={u.active ? '' : 'usr-row-inactive'}>
                    <td>
                      <div className="usr-user-cell">
                        <div className="usr-avatar" style={{ opacity: u.active ? 1 : 0.4 }}>
                          {(u.full_name || u.email || '?')[0].toUpperCase()}
                        </div>
                        <div className="usr-user-info">
                          <span className="usr-user-name">
                            {displayName(u)}
                            {isMe && <span className="usr-you-tag">tú</span>}
                          </span>
                          <span className="usr-user-email">{u.email || '—'}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      {isAdmin && !isMe ? (
                        <select
                          className="usr-role-select"
                          value={u.role}
                          disabled={changingRole === u.id}
                          onChange={(e) => changeRole(u.id, e.target.value as any)}
                          style={{ color: rc.color, borderColor: rc.color + '60' }}
                        >
                          <option value="admin">Administrador</option>
                          <option value="manager">Gestor</option>
                          <option value="viewer">Visualizador</option>
                        </select>
                      ) : (
                        <span
                          className="usr-role-badge"
                          style={{ color: rc.color, background: rc.bg, borderColor: rc.color + '40' }}
                        >
                          {ROLE_LABELS[u.role]}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`usr-status ${u.active ? 'active' : 'inactive'}`}>
                        <span className="usr-status-dot" />
                        {u.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="usr-td-date">
                      {new Date(u.created_at).toLocaleDateString('es-CL', {
                        year: 'numeric', month: 'short', day: 'numeric',
                      })}
                    </td>
                    {isAdmin && (
                      <td>
                        <div className="usr-actions">
                          {!isMe && (
                            <button
                              className={`usr-btn-toggle ${u.active ? 'deactivate' : 'activate'}`}
                              onClick={() => toggleActive(u.id, u.active)}
                              disabled={togglingId === u.id}
                              title={u.active ? 'Desactivar usuario' : 'Activar usuario'}
                            >
                              {togglingId === u.id
                                ? <span className="usr-spinner usr-spinner-sm" />
                                : u.active ? <BlockIcon /> : <CheckIcon />}
                              {u.active ? 'Desactivar' : 'Activar'}
                            </button>
                          )}
                          {!isMe && (
                            <button
                              className="usr-btn-cancel-inv"
                              onClick={() => setConfirmDeleteUser(u)}
                              disabled={deletingUserId === u.id}
                              title="Eliminar usuario"
                            >
                              {deletingUserId === u.id
                                ? <span className="usr-spinner usr-spinner-sm" />
                                : <CloseIcon />}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}

              {invitations.map((inv) => {
                const rc = ROLE_COLORS[inv.role];
                return (
                  <tr key={inv.id} className="usr-row-pending">
                    <td>
                      <div className="usr-user-cell">
                        <div className="usr-avatar usr-avatar-pending">?</div>
                        <div className="usr-user-info">
                          <span className="usr-user-name">{inv.email}</span>
                          <span className="usr-pending-tag">Invitación pendiente</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className="usr-role-badge"
                        style={{ color: rc.color, background: rc.bg, borderColor: rc.color + '40' }}
                      >
                        {ROLE_LABELS[inv.role]}
                      </span>
                    </td>
                    <td>
                      <span className="usr-status pending">
                        <span className="usr-status-dot" />
                        Pendiente
                      </span>
                    </td>
                    <td className="usr-td-date">
                      Invitado {new Date(inv.created_at).toLocaleDateString('es-CL', {
                        year: 'numeric', month: 'short', day: 'numeric',
                      })}
                    </td>
                    {isAdmin && (
                      <td>
                        <div className="usr-actions">
                          <button
                            className="usr-btn-copy"
                            onClick={() => copyLink(inv.token, inv.id)}
                            title="Copiar enlace de invitación"
                          >
                            {copiedId === inv.id ? <CheckIcon /> : <CopyIcon />}
                            {copiedId === inv.id ? 'Copiado' : 'Copiar enlace'}
                          </button>
                          <button
                            className="usr-btn-cancel-inv"
                            onClick={() => cancelInvitation(inv.id)}
                            disabled={deletingInvId === inv.id}
                            title="Cancelar invitación"
                          >
                            {deletingInvId === inv.id
                              ? <span className="usr-spinner usr-spinner-sm" />
                              : <CloseIcon />}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Toast ── */}
      {toast && <div className="usr-toast">{toast}</div>}

      {/* ── Invite Modal ── */}
      {inviteOpen && (
        <div className="usr-overlay" onClick={(e) => e.target === e.currentTarget && closeInviteModal()}>
          <div className="usr-modal">
            <div className="usr-modal-header">
              <h4>{createdToken ? 'Invitación creada' : 'Invitar usuario'}</h4>
              <button className="usr-modal-close" onClick={closeInviteModal}><CloseIcon /></button>
            </div>

            {createdToken ? (
              <div className="usr-invite-success">
                <div className="usr-invite-success-icon"><CheckCircleIcon /></div>
                <p className="usr-invite-success-title">¡Invitación enviada!</p>
                <p className="usr-invite-success-sub">
                  Se envió un correo a <strong>{inviteEmail}</strong> con el enlace para unirse.<br />
                  {isSuperAdmin && (
                    <>Empresa: <strong>{allCompanies.find((c) => c.id === inviteCompanyId)?.name ?? '—'}</strong><br /></>
                  )}
                  Si no llega, puedes compartir el enlace manualmente.
                </p>
                <div className="usr-link-box">
                  <span className="usr-link-text">
                    {`${window.location.origin}?join=${createdToken}`}
                  </span>
                  <button
                    className="usr-link-copy-btn"
                    onClick={() => copyLink(createdToken, 'modal')}
                  >
                    {copiedId === 'modal' ? <CheckIcon /> : <CopyIcon />}
                  </button>
                </div>
                <button className="usr-btn-save" onClick={closeInviteModal} style={{ width: '100%', marginTop: 16 }}>
                  Cerrar
                </button>
              </div>
            ) : (
              <form className="usr-form" onSubmit={handleInvite}>

                {/* Selector de empresa — solo visible para superadmin */}
                {isSuperAdmin && (
                  <div className="usr-field">
                    <label>Empresa <span className="usr-required">*</span></label>
                    {allCompanies.length === 0 ? (
                      <p className="usr-error">No hay empresas activas registradas.</p>
                    ) : (
                      <select
                        className="usr-company-select"
                        value={inviteCompanyId}
                        onChange={(e) => { setInviteCompanyId(e.target.value); setInviteError(''); }}
                        disabled={inviting}
                        autoFocus
                      >
                        {allCompanies.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                <div className="usr-field">
                  <label>Correo electrónico <span className="usr-required">*</span></label>
                  <input
                    type="email"
                    placeholder="usuario@empresa.com"
                    value={inviteEmail}
                    onChange={(e) => { setInviteEmail(e.target.value); setInviteError(''); }}
                    autoFocus={!isSuperAdmin}
                    disabled={inviting}
                  />
                </div>

                <div className="usr-field">
                  <label>Rol</label>
                  <div className="usr-role-options">
                    {(['admin', 'manager', 'viewer'] as const).map((r) => {
                      const rc = ROLE_COLORS[r];
                      return (
                        <label
                          key={r}
                          className={`usr-role-option ${inviteRole === r ? 'selected' : ''}`}
                          style={inviteRole === r ? { borderColor: rc.color, background: rc.bg } : {}}
                        >
                          <input
                            type="radio"
                            name="role"
                            value={r}
                            checked={inviteRole === r}
                            onChange={() => setInviteRole(r)}
                          />
                          <span className="usr-role-opt-name" style={{ color: inviteRole === r ? rc.color : undefined }}>
                            {ROLE_LABELS[r]}
                          </span>
                          <span className="usr-role-opt-desc">{ROLE_DESCS[r]}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {inviteError && <p className="usr-error">{inviteError}</p>}

                <div className="usr-modal-footer">
                  <button type="button" className="usr-btn-cancel" onClick={closeInviteModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="usr-btn-save" disabled={inviting}>
                    {inviting ? <span className="usr-spinner usr-spinner-sm" /> : <MailIcon />}
                    {inviting ? 'Creando…' : 'Enviar invitación'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Confirm delete user modal ── */}
      {confirmDeleteUser && (
        <div className="usr-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmDeleteUser(null)}>
          <div className="usr-modal">
            <div className="usr-modal-header">
              <h4>Eliminar usuario</h4>
              <button className="usr-modal-close" onClick={() => setConfirmDeleteUser(null)}><CloseIcon /></button>
            </div>
            <div style={{ padding: '0 24px 24px' }}>
              <p style={{ color: '#94a3b8', fontSize: '0.88rem', margin: '0 0 20px' }}>
                ¿Estás seguro de que deseas eliminar a{' '}
                <strong style={{ color: '#e2e8f0' }}>{displayName(confirmDeleteUser)}</strong>?
                Esta acción no se puede deshacer.
              </p>
              <div className="usr-modal-footer" style={{ padding: 0 }}>
                <button type="button" className="usr-btn-cancel" onClick={() => setConfirmDeleteUser(null)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="usr-btn-save"
                  onClick={handleDeleteUser}
                  disabled={deletingUserId === confirmDeleteUser.id}
                  style={{ background: '#ef4444' }}
                >
                  {deletingUserId === confirmDeleteUser.id ? <span className="usr-spinner usr-spinner-sm" /> : <CloseIcon />}
                  {deletingUserId === confirmDeleteUser.id ? 'Eliminando…' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function BlockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );
}
function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}
function LockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}
function MailIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}
function CheckCircleIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}
function SetupIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}
function EmptyUsersIcon() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}
